/**
 * Bazaarak REST API client — ASP.NET Core 9 backend
 * Production: leave VITE_API_BASE_URL empty (same-origin /api).
 * Dev: set to http://localhost:5000 or rely on Vite proxy with empty base.
 */

import { PLACEHOLDERS } from '@/lib/placeholders';

function resolveApiBaseFromEnv(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '').trim();
  if (!raw) return '';
  if (typeof window !== 'undefined') {
    const pointsToLocalDev = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(raw);
    if (pointsToLocalDev) {
      const host = window.location.hostname;
      if (host !== 'localhost' && host !== '127.0.0.1') return '';
    }
  }
  return raw;
}

/** API origin prefix; empty string = same host as the SPA (recommended on server). */
export function getApiBase(): string {
  return resolveApiBaseFromEnv();
}

const API_BASE = resolveApiBaseFromEnv();

// ─── Types ───────────────────────────────────────────────────

export interface LoginRequest {
  emailOrUsername: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  emailOrUsername: string;
  phone?: string;
  password: string;
  role?: 'customer' | 'vendor';
  storeName?: string;
}

export interface AuthResponse {
  token: string;
  role: string;
  username: string;
  userId: string;
  email?: string;
  avatar?: string;
}

export interface PaymentMethodResponse {
  id: string;
  type: string;
  cardNumber?: string;
  cardHolder?: string;
  expiryDate?: string;
  brand?: string;
  email?: string;
  bankName?: string;
  isDefault: boolean;
}

export interface NewAddress {
  label?: string;
  type?: number;
  fullName: string;
  phone?: string;
  street: string;
  city: string;
  state?: string;
  zipCode?: string;
  country?: string;
  isDefault?: boolean;
  latitude?: number | null;
  longitude?: number | null;
}

export interface CreateOrderRequest {
  shippingAddressId?: string;
  newAddress?: NewAddress;
  notes?: string;
}

export interface CreateOrderResponse {
  orderId: string;
  orderNumber: string;
  total: number;
  subtotal: number;
  shippingCost: number;
  tax: number;
  commissionAmount?: number;
  storeCount?: number;
  message?: string;
}

export interface PaymentSplitDto {
  type: string;
  accountId?: string;
  storeId?: string;
  amount: number;
  label?: string;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
  splits?: PaymentSplitDto[];
}

export interface PlatformSettings {
  deliveryFeePerKm: number;
  minDeliveryFee: number;
  courierDailyWageHint: number;
  commissionPercent: number;
  taxPercent: number;
  currency: string;
  allowedCurrencies?: string[];
}

export interface CartQuoteSuggestion {
  storeId: string;
  storeName: string;
  itemsSubtotal: number;
  deliveryFee: number;
  distanceKm: number;
  estimatedTotal: number;
  savings: number;
  message: string;
  replacementProductIds: string[];
}

export interface CartQuoteStoreGroup {
  storeId: string;
  storeName: string;
  storeAddress?: string;
  latitude?: number;
  longitude?: number;
  distanceKm: number;
  deliveryFee: number;
  itemsSubtotal: number;
  items: any[];
}

export interface CartQuoteResponse {
  groups: CartQuoteStoreGroup[];
  itemsSubtotal: number;
  shippingTotal: number;
  tax: number;
  commissionPreview: number;
  total: number;
  storeCount: number;
  isMultiStore: boolean;
  multiStoreWarning?: string;
  suggestions: CartQuoteSuggestion[];
  feePerKm: number;
  minDeliveryFee: number;
  commissionPercent: number;
  addressHasCoordinates: boolean;
}

export interface ProductOffer {
  productId: string;
  storeId: string;
  storeName: string;
  storeLogo?: string;
  price: number;
  discountPrice?: number | null;
  stock: number;
  rating?: number;
  distanceKm?: number;
}

/** Product aggregate returned by GET /api/product/{id} and /api/catalog/{id} */
export interface ProductAggregate {
  id: string;
  catalogProductId?: string;
  name: string;
  description?: string;
  price: number;
  stock: number;
  offers: ProductOffer[];
  selectedOfferId?: string;
  fromPrice?: number;
  toPrice?: number;
  offerCount?: number;
  [key: string]: unknown;
}

export interface StripeStatusResponse {
  hasStripeAccount: boolean;
  status: string;
  onboardingLink?: string;
  isActive: boolean;
  canSell: boolean;
  accountId?: string;
}

export interface StartOnboardingResponse {
  success: boolean;
  requiresOnboarding?: boolean;
  onboardingUrl?: string;
  isActive?: boolean;
  message?: string;
}

// ─── Helpers ─────────────────────────────────────────────────

function isSameOriginAbsoluteUrl(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** Paths served by the API / Vite proxy (never treat as third-party). */
const LOCAL_MEDIA_PREFIXES = [
  '/uploads/',
  '/seed-media/',
  '/placeholders/',
  '/campaign/',
  '/OSM/',
  '/images/',
  '/fonts/',
  '/assets/',
] as const;

function isLocalMediaPath(pathname: string): boolean {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return LOCAL_MEDIA_PREFIXES.some(
    (p) => path === p.slice(0, -1) || path.startsWith(p)
  );
}

/**
 * Turn absolute API-host media URLs into same-origin paths so Vite proxy
 * (dev) and ASP.NET static files (prod) can serve them.
 * e.g. http://localhost:5000/uploads/a.jpg → /uploads/a.jpg
 */
function toServableMediaUrl(url: string): string | null {
  try {
    if (url.startsWith('uploads/') || url.startsWith('seed-media/') || url.startsWith('placeholders/')) {
      return `/${url}`;
    }
    if (url.startsWith('/')) {
      return url;
    }
    if (!/^https?:\/\//i.test(url)) return null;

    const parsed = new URL(url);
    const pathWithQuery = `${parsed.pathname}${parsed.search}`;
    if (isLocalMediaPath(parsed.pathname)) {
      return pathWithQuery;
    }
    if (typeof window !== 'undefined' && parsed.origin === window.location.origin) {
      return pathWithQuery;
    }
    // Dev: API on another localhost port still counts as our media host
    if (
      /^(localhost|127\.0\.0\.1)$/i.test(parsed.hostname) &&
      isLocalMediaPath(parsed.pathname)
    ) {
      return pathWithQuery;
    }
    return null;
  } catch {
    return null;
  }
}

/** Resolve upload paths; rewrite API absolute media URLs; block unknown third-party hosts. */
export const resolveImageUrl = (url?: string | null, fallback?: string): string => {
  const fb = fallback || PLACEHOLDERS.product;
  if (!url?.trim()) return fb;
  const trimmed = url.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;

  const local = toServableMediaUrl(trimmed);
  if (local) {
    const base = getApiBase();
    // Absolute same-origin already reduced to path; optional API base for cross-host setups
    if (base && local.startsWith('/') && isLocalMediaPath(local.split('?')[0])) {
      // Only prefix API base for backend-hosted media, not Vite /assets
      if (
        local.startsWith('/uploads') ||
        local.startsWith('/seed-media') ||
        local.startsWith('/placeholders') ||
        local.startsWith('/campaign') ||
        local.startsWith('/OSM') ||
        local.startsWith('/images')
      ) {
        return `${base}${local}`;
      }
    }
    return local;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return isSameOriginAbsoluteUrl(trimmed) ? trimmed : fb;
  }

  return trimmed;
};

const getToken = (): string | null => {
  const t = localStorage.getItem('token');
  return t && t.trim() ? t.trim() : null;
};

const removeToken = () => {
  [
    'token', 'isLoggedIn', 'userType', 'userName', 'userId',
    'userEmail', 'userPhone', 'userAvatar', 'vendorId',
  ].forEach((k) => localStorage.removeItem(k));
};

const persistAuth = (auth: AuthResponse) => {
  localStorage.setItem('token', auth.token);
  localStorage.setItem('isLoggedIn', 'true');
  localStorage.setItem('userType', auth.role);
  localStorage.setItem('userName', auth.username || '');
  localStorage.setItem('userId', String(auth.userId));
  if (auth.email) localStorage.setItem('userEmail', auth.email);
  if (auth.avatar) localStorage.setItem('userAvatar', auth.avatar);
};

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function request<T = unknown>(
  path: string,
  options: RequestInit & { auth?: boolean; formData?: boolean } = {},
): Promise<T> {
  const { auth, formData = false, headers: extra, ...rest } = options;
  const headers = new Headers(extra || {});

  if (!formData && rest.body && !(rest.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getToken();
  if (auth === true) {
    if (!token) throw new ApiError('Unauthorized', 401);
    headers.set('Authorization', `Bearer ${token}`);
  } else if (auth !== false && token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Always send current UI language for localized product/content responses
  try {
    const lang =
      (typeof localStorage !== 'undefined' && localStorage.getItem('bazaarak_language')) ||
      (typeof document !== 'undefined' && document.documentElement.lang) ||
      'en';
    if (!headers.has('Accept-Language')) headers.set('Accept-Language', lang);
  } catch {
    /* ignore */
  }

  const res = await fetch(`${API_BASE}${path}`, { ...rest, headers });

  if (res.status === 401) {
    if (auth === true) removeToken();
    const errBody = await res.json().catch(() => ({}));
    throw new ApiError((errBody as { message?: string }).message || 'Unauthorized', 401);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new ApiError(
      (errBody as { message?: string }).message || `Request failed (${res.status})`,
      res.status,
    );
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

const normalizeOffer = (o: any): ProductOffer | null => {
  if (!o || typeof o !== 'object') return null;
  const productId = String(o.productId ?? o.product_id ?? o.id ?? '');
  if (!productId) return null;
  const discountRaw = o.discountPrice ?? o.discount_price;
  return {
    productId,
    storeId: String(o.storeId ?? o.store_id ?? ''),
    storeName: o.storeName ?? o.store_name ?? o.vendor ?? '',
    storeLogo: o.storeLogo ?? o.store_logo ?? o.logo ?? undefined,
    price: Number(o.price ?? 0),
    discountPrice: discountRaw != null ? Number(discountRaw) : null,
    stock: Number(o.stock ?? o.stockQuantity ?? 0),
    rating: o.rating != null ? Number(o.rating) : undefined,
    distanceKm: o.distanceKm != null ? Number(o.distanceKm) : undefined,
  };
};

/** Attach normalized offers[] + catalog aggregate fields (fromPrice, offerCount, catalogProductId, selectedOfferId) */
const withCatalogFields = (p: any) => {
  if (!p || typeof p !== 'object') return p;
  const offers: ProductOffer[] = Array.isArray(p.offers)
    ? (p.offers.map(normalizeOffer).filter(Boolean) as ProductOffer[])
    : [];
  const offerCountRaw = p.offerCount ?? p.offer_count ?? (offers.length || undefined);
  const fromPriceRaw =
    p.fromPrice ?? p.from_price ??
    (offers.length
      ? Math.min(...offers.map((o) => (o.discountPrice ?? o.price)))
      : undefined);
  const toPriceRaw =
    p.toPrice ?? p.to_price ??
    (offers.length
      ? Math.max(...offers.map((o) => (o.discountPrice ?? o.price)))
      : undefined);
  const catalogProductId =
    p.catalogProductId != null
      ? String(p.catalogProductId)
      : p.catalog_product_id != null
        ? String(p.catalog_product_id)
        : undefined;
  const selectedOfferId =
    p.selectedOfferId != null
      ? String(p.selectedOfferId)
      : p.selected_offer_id != null
        ? String(p.selected_offer_id)
        : undefined;
  return {
    ...p,
    offers,
    offerCount: offerCountRaw != null ? Number(offerCountRaw) : undefined,
    fromPrice: fromPriceRaw != null ? Number(fromPriceRaw) : undefined,
    toPrice: toPriceRaw != null ? Number(toPriceRaw) : undefined,
    catalogProductId,
    selectedOfferId,
  };
};

/** Normalize product fields from backend quirks */
const normalizeProduct = (p: any) => {
  if (!p || typeof p !== 'object') return p;
  const discountEnabled =
    p.discountEnabled ?? p.discount_enabled ?? p.discount_Enabled ?? false;
  const discountPrice =
    p.discountPrice ?? p.discount_price ?? p.discount_Price ?? null;

  let rawMedia = p.media;
  if (typeof rawMedia === 'string') {
    try {
      rawMedia = JSON.parse(rawMedia);
    } catch {
      rawMedia = [];
    }
  }

  const images: string[] = Array.isArray(p.images)
    ? p.images
    : Array.isArray(rawMedia)
      ? rawMedia.map((m: any) => (typeof m === 'string' ? m : m?.url)).filter(Boolean)
      : p.image
        ? [p.image]
        : [];
  const image = p.image || images[0] || '';
  return withCatalogFields({
    ...p,
    discountEnabled,
    discount_enabled: discountEnabled,
    discountPrice,
    discount_price: discountPrice,
    images,
    image,
    media: Array.isArray(rawMedia)
      ? rawMedia
      : images.map((url) => ({ url, type: 'image' })),
    price: Number(p.price ?? 0),
    stock: Number(p.stock ?? 0),
    rating: Number(p.rating ?? 0),
    reviews: Number(p.reviews ?? 0),
  });
};

const normalizeProducts = (list: any) =>
  Array.isArray(list) ? list.map(normalizeProduct) : [];

// ─── Auth ────────────────────────────────────────────────────

export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  const auth = await request<AuthResponse>('/api/auth/login', {
    method: 'POST',
    auth: false,
    body: JSON.stringify(data),
  });
  persistAuth(auth);
  return auth;
};

export const register = async (data: RegisterRequest): Promise<AuthResponse> => {
  const auth = await request<AuthResponse>('/api/auth/register', {
    method: 'POST',
    auth: false,
    body: JSON.stringify(data),
  });
  persistAuth(auth);
  return auth;
};

export const getAuthMe = async () => {
  try {
    return await request<any>('/api/auth/me', { auth: true });
  } catch {
    return null;
  }
};

export const logout = () => {
  removeToken();
};

// ─── Profile ─────────────────────────────────────────────────

export const getProfile = async () => {
  try {
    return await request<any>('/api/user/profile', { auth: true });
  } catch (err: any) {
    if (err?.status === 401) throw err;
    return null;
  }
};

export const updateProfile = async (profileData: {
  name: string;
  email: string;
  phone: string;
  address?: string;
  dateOfBirth?: string;
  avatar?: string;
}) => {
  return request<{ message: string }>('/api/user/profile', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(profileData),
  });
};

export const applyReferralCode = async (code: string) => {
  return request<{ message: string; error?: boolean }>('/api/user/referral', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ code }),
  });
};

export const upgradePro = async (days = 30) => {
  return request<{ message: string; error?: boolean }>('/api/user/pro/upgrade', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ days }),
  });
};

// ─── Cart ────────────────────────────────────────────────────

export const getCart = async () => {
  try {
    return await request<{ items: any[]; total: number }>('/api/user/cart', { auth: true });
  } catch (err: any) {
    if (err?.status === 401) throw err;
    return { items: [], total: 0 };
  }
};

export const addToCart = async (
  productId: string,
  quantity: number,
  variantSelected: string = '',
) => {
  const result = await request<{ message: string }>('/api/user/cart', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ productId, quantity, variantSelected }),
  });
  window.dispatchEvent(new Event('cartUpdated'));
  return result;
};

export const updateCartItem = async (productId: string, quantity: number) => {
  const result = await request<{ message: string }>(`/api/user/cart/${productId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ quantity }),
  });
  window.dispatchEvent(new Event('cartUpdated'));
  return result;
};

export const removeFromCart = async (productId: string) => {
  const result = await request<{ message: string }>(`/api/user/cart/${productId}`, {
    method: 'DELETE',
    auth: true,
  });
  window.dispatchEvent(new Event('cartUpdated'));
  return result;
};

export const getCartQuote = async (shippingAddressId?: string | null) => {
  return request<CartQuoteResponse>('/api/user/cart/quote', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ shippingAddressId: shippingAddressId ?? null }),
  });
};

export const applyCartSuggestion = async (
  storeId: string,
  replacementProductIds?: string[],
) => {
  const result = await request<{ message: string }>('/api/user/cart/apply-suggestion', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ storeId, replacementProductIds }),
  });
  window.dispatchEvent(new Event('cartUpdated'));
  return result;
};

// ─── Wishlist ────────────────────────────────────────────────

export const getWishlist = async () => {
  try {
    const data = await request<any[]>('/api/user/wishlist', { auth: true });
    return normalizeProducts(data);
  } catch (err: any) {
    if (err?.status === 401) throw err;
    return [];
  }
};

export const addToWishlist = async (productId: string) => {
  const result = await request<{ message: string }>('/api/user/wishlist', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ productId }),
  });
  window.dispatchEvent(new Event('wishlistUpdated'));
  return result;
};

export const removeFromWishlist = async (productId: string) => {
  const result = await request<{ message: string }>(`/api/user/wishlist/${productId}`, {
    method: 'DELETE',
    auth: true,
  });
  window.dispatchEvent(new Event('wishlistUpdated'));
  return result;
};

// ─── Addresses ───────────────────────────────────────────────

export const getAddresses = async () => {
  try {
    return await request<any[]>('/api/user/addresses', { auth: true });
  } catch {
    return [];
  }
};

export const createAddress = async (addressData: any) => {
  return request<{ id?: string; message: string }>('/api/user/addresses', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({
      label: addressData.label ?? 'Home',
      fullName: addressData.fullName ?? addressData.full_name,
      phone: addressData.phone,
      street: addressData.street ?? addressData.address,
      city: addressData.city,
      state: addressData.state,
      zipCode: addressData.zipCode ?? addressData.zip ?? addressData.zip_code,
      country: addressData.country ?? 'Iran',
      type: addressData.type ?? 0,
      isDefault: addressData.isDefault ?? addressData.is_default ?? false,
      latitude: addressData.latitude ?? addressData.lat ?? null,
      longitude: addressData.longitude ?? addressData.lng ?? addressData.lon ?? null,
    }),
  });
};

export const updateAddress = async (addressId: string, addressData: any) => {
  return request<{ message: string }>(`/api/user/addresses/${addressId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({
      label: addressData.label ?? 'Home',
      fullName: addressData.fullName ?? addressData.full_name,
      phone: addressData.phone,
      street: addressData.street ?? addressData.address,
      city: addressData.city,
      state: addressData.state,
      zipCode: addressData.zipCode ?? addressData.zip ?? addressData.zip_code,
      country: addressData.country ?? 'Iran',
      type: addressData.type ?? 0,
      isDefault: addressData.isDefault ?? addressData.is_default ?? false,
      latitude: addressData.latitude ?? addressData.lat ?? null,
      longitude: addressData.longitude ?? addressData.lng ?? addressData.lon ?? null,
    }),
  });
};

export const deleteAddress = async (addressId: string) => {
  return request<{ message: string }>(`/api/user/addresses/${addressId}`, {
    method: 'DELETE',
    auth: true,
  });
};

// ─── Orders (user) ───────────────────────────────────────────

export const getOrders = async (page: number = 1, pageSize: number = 10) => {
  try {
    return await request<{ orders: any[]; totalCount: number }>(
      `/api/user/orders?page=${page}&pageSize=${pageSize}`,
      { auth: true },
    );
  } catch (err: any) {
    if (err?.status === 401) throw err;
    return { orders: [], totalCount: 0 };
  }
};

export const getOrderById = async (orderId: string) => {
  try {
    return await request<any>(`/api/user/orders/${orderId}`, { auth: true });
  } catch {
    return null;
  }
};

// ─── Notifications ───────────────────────────────────────────

export const getNotifications = async (page: number = 1, pageSize: number = 20) => {
  try {
    return await request<{ notifications: any[]; unreadCount: number; totalCount: number }>(
      `/api/user/notifications?page=${page}&pageSize=${pageSize}`,
      { auth: true },
    );
  } catch (err: any) {
    if (err?.status === 401) throw err;
    return { notifications: [], unreadCount: 0, totalCount: 0 };
  }
};

export const markNotificationAsRead = async (notificationId: string) => {
  return request<{ message: string }>(`/api/user/notifications/${notificationId}/read`, {
    method: 'PUT',
    auth: true,
  });
};

export const markAllNotificationsAsRead = async () => {
  return request<{ message: string }>('/api/user/notifications/read-all', {
    method: 'PUT',
    auth: true,
  });
};

export const deleteNotification = async (notificationId: string) => {
  return request<{ message: string }>(`/api/user/notifications/${notificationId}`, {
    method: 'DELETE',
    auth: true,
  });
};

// ─── Reviews ─────────────────────────────────────────────────

export const createReview = async (reviewData: {
  productId: string | number;
  orderId?: string | number | null;
  rating: number;
  title?: string;
  comment?: string;
  images?: string[];
}) => {
  return request<{ message: string }>('/api/user/reviews', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({
      productId: reviewData.productId,
      orderId: reviewData.orderId || null,
      rating: reviewData.rating,
      title: reviewData.title || '',
      comment: reviewData.comment || '',
      images: reviewData.images,
    }),
  });
};

export const getUserReviews = async () => {
  try {
    return await request<any[]>('/api/user/reviews', { auth: true });
  } catch {
    return [];
  }
};

export const getProductReviews = async (productId: string) => {
  try {
    return await request<any[]>(`/api/product/${productId}/reviews`);
  } catch {
    return [];
  }
};

export const getStoreReviews = async (storeId: string) => {
  try {
    // Prefer product reviews filtered by store when dedicated endpoint is absent
    const store = await getPublicStore(storeId);
    if (!store) return [];
    return [];
  } catch {
    return [];
  }
};

// ─── Products ────────────────────────────────────────────────

export const getProductById = async (id: string) => {
  try {
    const p = await request<any>(`/api/product/${id}`);
    return normalizeProduct(p);
  } catch {
    return null;
  }
};

/** Load a product aggregate by catalog id. The public product endpoint also
 * accepts a catalog id, so this falls back to it when the dedicated route is absent. */
export const getCatalogById = async (catalogId: string) => {
  try {
    const p = await request<any>(`/api/catalog/${catalogId}`);
    return normalizeProduct(p);
  } catch {
    return getProductById(catalogId);
  }
};

export const getFeaturedProducts = async (limit: number = 10, country?: string) => {
  try {
    const q = new URLSearchParams({ limit: String(limit) });
    if (country) q.set('country', country);
    return normalizeProducts(await request<any[]>(`/api/product/featured?${q}`));
  } catch {
    return [];
  }
};

/** Location + timezone rail (admin algorithm scope: landing_local). */
export const getLocalFeaturedProducts = async (opts: {
  limit?: number;
  country?: string;
  city?: string;
  lat?: number;
  lng?: number;
  tz?: string;
} = {}) => {
  try {
    const q = new URLSearchParams({ limit: String(opts.limit ?? 12) });
    if (opts.country) q.set('country', opts.country);
    if (opts.city) q.set('city', opts.city);
    if (opts.lat != null && !Number.isNaN(opts.lat)) q.set('lat', String(opts.lat));
    if (opts.lng != null && !Number.isNaN(opts.lng)) q.set('lng', String(opts.lng));
    if (opts.tz) q.set('tz', opts.tz);
    return normalizeProducts(await request<any[]>(`/api/product/featured-local?${q}`));
  } catch {
    return [];
  }
};

export const getTrendingProducts = async (limit: number = 10) => {
  try {
    return normalizeProducts(await request<any[]>(`/api/product/trending?limit=${limit}`));
  } catch {
    return [];
  }
};

export const getOffers = async (limit: number = 10) => {
  try {
    return normalizeProducts(await request<any[]>(`/api/product/offers?limit=${limit}`));
  } catch {
    return [];
  }
};

export const getProductsByCategory = async (
  categoryId: string,
  page: number = 1,
  pageSize: number = 20,
  sortBy?: string,
) => {
  try {
    const qs = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (sortBy) qs.set('sortBy', sortBy);
    return normalizeProducts(
      await request<any[]>(`/api/product/by-category/${categoryId}?${qs}`),
    );
  } catch {
    return [];
  }
};

export const searchProducts = async (
  q: string,
  limit?: number,
  categoryId?: string,
  minPrice?: number,
  maxPrice?: number,
  brand?: string,
  color?: string,
  size?: string,
) => {
  try {
    const qs = new URLSearchParams({ q: q || '' });
    if (limit != null) qs.set('limit', String(limit));
    if (categoryId != null) qs.set('categoryId', String(categoryId));
    if (minPrice != null) qs.set('minPrice', String(minPrice));
    if (maxPrice != null) qs.set('maxPrice', String(maxPrice));
    if (brand) qs.set('brand', brand);
    if (color) qs.set('color', color);
    if (size) qs.set('size', size);
    const data = await request<{ products: any[]; totalCount: number }>(
      `/api/product/search?${qs}`,
    );
    return {
      products: normalizeProducts(data.products || []),
      totalCount: data.totalCount ?? 0,
    };
  } catch {
    return { products: [], totalCount: 0 };
  }
};

// ─── Categories ──────────────────────────────────────────────

export const getAllCategories = async () => {
  try {
    return await request<any[]>('/api/category');
  } catch {
    return [];
  }
};

export const getSubcategories = async (parentId: string) => {
  try {
    return await request<any[]>(`/api/category/${parentId}/subcategories`);
  } catch {
    return [];
  }
};

export const getCategoryById = async (id: string) => {
  try {
    return await request<any>(`/api/category/${id}`);
  } catch {
    return null;
  }
};

// ─── Stories ─────────────────────────────────────────────────

function migrateSlideItem(item: any): any {
  if (!item) return item;
  if (typeof item === 'string') return { type: 'image', url: item };
  return item;
}

function migrateSlide(slide: any): any {
  if (!slide) return slide;
  if (typeof slide === 'string') return { image: slide, title: '', link: '' };
  if (Array.isArray(slide.items)) {
    return { ...slide, items: slide.items.map(migrateSlideItem) };
  }
  return slide;
}

const normalizeStories = (list: any[]) =>
  (list || []).map((s) => {
    let slides = s.slides;
    if (typeof slides === 'string') {
      try {
        slides = JSON.parse(slides);
      } catch {
        slides = [];
      }
    }
    if (!Array.isArray(slides)) slides = [];
    return {
      ...s,
      slides: slides.map(migrateSlide),
    };
  });

export const getActiveStories = async () => {
  try {
    return normalizeStories(await request<any[]>('/api/story/active'));
  } catch {
    return [];
  }
};

export const getVendorStories = async (vendorId: string) => {
  try {
    return normalizeStories(await request<any[]>(`/api/story/vendor/${vendorId}`));
  } catch {
    return [];
  }
};

export const incrementStoryView = async (_id: string) => {
  // Backend has no dedicated endpoint yet — no-op for UX
  return { message: 'ok' };
};

// ─── Payment methods ─────────────────────────────────────────

export const getPaymentMethods = async (): Promise<PaymentMethodResponse[]> => {
  try {
    return await request<PaymentMethodResponse[]>('/api/paymentmethod', { auth: true });
  } catch {
    return [];
  }
};

export const getPaymentMethodById = async (id: string): Promise<PaymentMethodResponse | null> => {
  try {
    return await request<PaymentMethodResponse>(`/api/paymentmethod/${id}`, { auth: true });
  } catch {
    return null;
  }
};

export const createPaymentMethod = async (data: any) => {
  return request<{ message: string; id?: string; isDefault?: boolean }>('/api/paymentmethod', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(data),
  });
};

export const updatePaymentMethod = async (id: string, data: any) => {
  return request<{ message: string }>(`/api/paymentmethod/${id}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(data),
  });
};

export const setDefaultPaymentMethod = async (id: string) => {
  return request<{ message: string }>(`/api/paymentmethod/${id}/default`, {
    method: 'PUT',
    auth: true,
  });
};

export const deletePaymentMethod = async (id: string) => {
  return request<{ message: string }>(`/api/paymentmethod/${id}`, {
    method: 'DELETE',
    auth: true,
  });
};

// ─── Vendor ──────────────────────────────────────────────────

export const getVendorStore = async () => {
  try {
    return await request<any>('/api/vendor/store-info', { auth: true });
  } catch {
    return null;
  }
};

export const updateVendorStore = async (storeData: any) => {
  return request<{ message: string }>('/api/vendor/store-info', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({
      name: storeData.name,
      description: storeData.description,
      logo: storeData.logo,
      banner: storeData.banner,
      address: storeData.address,
      city: storeData.city,
      phone: storeData.phone,
      email: storeData.email,
      latitude: storeData.latitude ?? storeData.lat ?? null,
      longitude: storeData.longitude ?? storeData.lng ?? storeData.lon ?? null,
    }),
  });
};

export const getVendorProducts = async () => {
  try {
    return normalizeProducts(await request<any[]>('/api/vendor/products', { auth: true }));
  } catch {
    return [];
  }
};

export const createVendorProduct = async (productData: any) => {
  const media = normalizeVendorMedia(productData);
  const catalogProductId = productData.catalogProductId ?? productData.catalog_product_id ?? null;
  const barcode = productData.barcode ?? null;
  return request<{ id?: number | string; message: string }>('/api/vendor/products', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({
      name: productData.name,
      description: productData.description,
      price: Number(productData.price),
      stock: Number(productData.stock ?? 0),
      category: productData.category,
      categoryId: productData.categoryId ?? productData.category_id,
      images: media.filter((m) => m.type === 'image').map((m) => m.url),
      media,
      variants: productData.variants,
      discountEnabled: productData.discountEnabled ?? productData.discount_enabled ?? false,
      discountPrice: productData.discountPrice ?? productData.discount_price,
      status: productData.status ?? 'active',
      // Multi-vendor catalog linking: link to an existing catalog identity,
      // or send a barcode so backend can find-or-create one.
      ...(catalogProductId ? { catalogProductId } : {}),
      ...(barcode ? { barcode } : {}),
      ...(productData.currency ? { currency: productData.currency } : {}),
    }),
  });
};

export const updateVendorProduct = async (id: number | string, productData: any) => {
  const media = normalizeVendorMedia(productData);
  const catalogProductId = productData.catalogProductId ?? productData.catalog_product_id ?? null;
  const barcode = productData.barcode ?? null;
  return request<{ message: string }>(`/api/vendor/products/${id}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({
      name: productData.name,
      description: productData.description,
      price: Number(productData.price),
      stock: Number(productData.stock ?? 0),
      category: productData.category,
      categoryId: productData.categoryId ?? productData.category_id,
      images: media.filter((m) => m.type === 'image').map((m) => m.url),
      media,
      variants: productData.variants,
      discountEnabled: productData.discountEnabled ?? productData.discount_enabled ?? false,
      discountPrice: productData.discountPrice ?? productData.discount_price,
      status: productData.status ?? 'active',
      ...(catalogProductId ? { catalogProductId } : {}),
      ...(barcode ? { barcode } : {}),
      ...(productData.currency ? { currency: productData.currency } : {}),
    }),
  });
};

// ─── Vendor catalog (multi-vendor product identity) ──────────

export interface CatalogSearchResult {
  catalogProductId: string;
  name: string;
  description?: string;
  categoryId?: string | null;
  barcode?: string | null;
  image?: string | null;
  offerCount?: number;
  fromPrice?: number | null;
}

const normalizeCatalogResult = (c: any): CatalogSearchResult => ({
  catalogProductId: String(c.catalogProductId ?? c.catalog_product_id ?? c.id ?? ''),
  name: c.name ?? '',
  description: c.description ?? '',
  categoryId: c.categoryId ?? c.category_id ?? null,
  barcode: c.barcode ?? null,
  image:
    c.image ??
    (Array.isArray(c.media)
      ? (typeof c.media[0] === 'string' ? c.media[0] : c.media[0]?.url)
      : null) ??
    null,
  offerCount: c.offerCount ?? c.offer_count ?? 0,
  fromPrice: c.fromPrice ?? c.from_price ?? null,
});

/** Suggest catalog matches for the vendor create form (search by name/barcode). */
export const searchVendorCatalog = async (
  q: string,
  limit: number = 10,
): Promise<CatalogSearchResult[]> => {
  try {
    const qs = new URLSearchParams({ q: q || '' });
    if (limit != null) qs.set('limit', String(limit));
    const data = await request<any[]>(`/api/vendor/catalog/search?${qs}`, { auth: true });
    return Array.isArray(data) ? data.map(normalizeCatalogResult) : [];
  } catch {
    return [];
  }
};

function normalizeVendorMedia(productData: any): { url: string; type: string; isDefault: boolean; sortOrder: number }[] {
  if (Array.isArray(productData.media) && productData.media.length > 0) {
    return productData.media
      .filter((m: any) => m?.url)
      .map((m: any, i: number) => ({
        url: m.url,
        type: m.type === 'video' || m.type === 'model' ? m.type : 'image',
        isDefault: !!m.isDefault,
        sortOrder: m.sortOrder ?? i,
      }));
  }
  const images: string[] = Array.isArray(productData.images)
    ? productData.images
    : productData.image
      ? [productData.image]
      : [];
  return images.filter(Boolean).map((url, i) => ({
    url,
    type: 'image',
    isDefault: i === 0,
    sortOrder: i,
  }));
}

export const deleteVendorProduct = async (id: string) => {
  return request<{ message: string }>(`/api/vendor/products/${id}`, {
    method: 'DELETE',
    auth: true,
  });
};

export const getVendorStoriesList = async () => {
  try {
    const list = await request<any[]>('/api/vendor/stories', { auth: true });
    return (list || []).map((s) => {
      let slides = s.slides;
      if (typeof slides === 'string') {
        try {
          slides = JSON.parse(slides);
        } catch {
          slides = [];
        }
      }
      return { ...s, slides };
    });
  } catch {
    return [];
  }
};

export const createVendorStory = async (storyData: any) => {
  return request<{ id?: number; message: string }>('/api/vendor/stories', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({
      title: storyData.title,
      slides: storyData.slides,
      isActive: storyData.isActive ?? true,
      expiresInHours: storyData.expiresInHours ?? 24,
    }),
  });
};

export const updateVendorStory = async (id: string, storyData: any) => {
  return request<{ message: string }>(`/api/vendor/stories/${id}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(storyData),
  });
};

export const getVendorStoryById = async (id: string) => {
  try {
    const list = await getVendorStoriesList();
    return list.find((s: any) => s.id === id) || null;
  } catch {
    return null;
  }
};

export const deleteVendorStory = async (id: string) => {
  return request<{ message: string }>(`/api/vendor/stories/${id}`, {
    method: 'DELETE',
    auth: true,
  });
};

export const toggleVendorStory = async (id: string) => {
  return request<{ message: string; isActive?: boolean }>(
    `/api/vendor/stories/${id}/toggle`,
    { method: 'PATCH', auth: true },
  );
};

export const getVendorStats = async () => {
  try {
    return await request<any>('/api/vendor/dashboard-stats', { auth: true });
  } catch {
    return {
      totalSales: 0,
      totalOrders: 0,
      totalProducts: 0,
      totalCustomers: 0,
      salesChange: 0,
      ordersChange: 0,
      productsChange: 0,
      customersChange: 0,
    };
  }
};

export const getVendorStripeStatus = async (): Promise<StripeStatusResponse> => {
  try {
    return await request<StripeStatusResponse>('/api/vendor/stripe-status', { auth: true });
  } catch {
    return {
      hasStripeAccount: false,
      status: 'not_connected',
      isActive: false,
      canSell: false,
    };
  }
};

export const startVendorStripeOnboarding = async (
  email: string,
  returnUrl?: string,
): Promise<StartOnboardingResponse> => {
  return request<StartOnboardingResponse>('/api/vendor/stripe-onboarding', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ email, returnUrl }),
  });
};

export const refreshVendorStripeStatus = async (): Promise<StripeStatusResponse> => {
  try {
    return await request<StripeStatusResponse>('/api/vendor/stripe-refresh-status', {
      auth: true,
    });
  } catch {
    return getVendorStripeStatus();
  }
};

export const resetVendorStripeAccount = async (): Promise<{ message: string }> => {
  return request<{ message: string }>('/api/vendor/stripe-reset', {
    method: 'POST',
    auth: true,
  });
};

// ─── Courier / Delivery workflow ─────────────────────────────

export const getVendorCouriers = async () => {
  try {
    return await request<{ couriers: { id: string; name: string }[] }>('/api/vendor/couriers', { auth: true });
  } catch {
    return { couriers: [] };
  }
};

export const getVendorDeliveryLegs = async () => {
  try {
    return await request<{ legs: any[] }>('/api/vendor/delivery-legs', { auth: true });
  } catch {
    return { legs: [] };
  }
};

export const assignCourierToDeliveryLeg = async (legId: string, data: { courierId: string; initialStatus?: string; courierNote?: string }) => {
  return request<{ message: string }>(`/api/vendor/delivery-legs/${legId}/assign-courier`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({
      courierId: data.courierId,
      initialStatus: data.initialStatus,
      courierNote: data.courierNote,
    }),
  });
};

export const getCourierMyDeliveryLegs = async () => {
  try {
    return await request<{ legs: any[] }>('/api/courier/my-legs', { auth: true });
  } catch {
    return { legs: [] };
  }
};

export const updateCourierDeliveryLeg = async (legId: string, data: { status: string; trackingNumber?: string; courierNote?: string }) => {
  return request<{ message: string }>(`/api/courier/legs/${legId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({
      status: data.status,
      trackingNumber: data.trackingNumber,
      courierNote: data.courierNote,
    }),
  });
};

export const getCourierDashboard = async () =>
  request<any>('/api/courier/dashboard', { auth: true });

export const setCourierPresence = async (online: boolean, onDuty?: boolean) =>
  request<{ online: boolean; onDuty: boolean }>('/api/courier/presence', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ online, onDuty }),
  });

export const updateCourierLocation = async (latitude: number, longitude: number) =>
  request('/api/courier/location', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ latitude, longitude }),
  });

export const getCourierNearby = async () =>
  request<{ courier: any; offers: any[]; legend: any[] }>('/api/courier/nearby', { auth: true });

export const acceptCourierOffer = async (legId: string) =>
  request(`/api/courier/offers/${legId}/accept`, { method: 'POST', auth: true });

export const rejectCourierOffer = async (legId: string) =>
  request(`/api/courier/offers/${legId}/reject`, { method: 'POST', auth: true });

export const getCourierOrders = async (filter?: string) =>
  request<{ stats: any; orders: any[] }>(
    `/api/courier/orders${filter ? `?filter=${encodeURIComponent(filter)}` : ''}`,
    { auth: true },
  );

export const getCourierIncome = async (period?: string) =>
  request<any>(`/api/courier/income${period ? `?period=${encodeURIComponent(period)}` : ''}`, {
    auth: true,
  });

export const getCourierProfile = async () =>
  request<any>('/api/courier/profile', { auth: true });

export const updateCourierProfile = async (body: Record<string, unknown>) =>
  request('/api/courier/profile', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(body),
  });

export const updateCourierVehicle = async (body: Record<string, unknown>) =>
  request('/api/courier/vehicle', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(body),
  });

// ─── Orders / Payment ────────────────────────────────────────

export const createOrderFromCart = async (
  orderRequest: CreateOrderRequest,
): Promise<CreateOrderResponse> => {
  const result = await request<CreateOrderResponse>('/api/order/create-from-cart', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(orderRequest),
  });
  window.dispatchEvent(new Event('cartUpdated'));
  return result;
};

export const getOrderDetail = async (orderId: string) => {
  try {
    return await request<any>(`/api/order/${orderId}`, { auth: true });
  } catch {
    return getOrderById(orderId);
  }
};

export const getMyOrders = async (page: number = 1, pageSize: number = 10) => {
  try {
    return await request<{ orders: any[]; totalCount: number }>(
      `/api/order/my-orders?page=${page}&pageSize=${pageSize}`,
      { auth: true },
    );
  } catch {
    return getOrders(page, pageSize);
  }
};

export const createCheckoutSession = async (
  orderId: string,
): Promise<CreateCheckoutSessionResponse> => {
  return request<CreateCheckoutSessionResponse>('/api/payment/create-checkout-session', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ orderId }),
  });
};

export const getOrderPaymentStatus = async (orderId: string) => {
  try {
    return await request<{
      status: string;
      isPaid: boolean;
      splits?: PaymentSplitDto[];
    }>(`/api/payment/order-status/${orderId}`, { auth: true });
  } catch {
    return { status: 'unknown', isPaid: false, splits: [] };
  }
};

// ─── Public stores ───────────────────────────────────────────

export const getPublicStore = async (storeId: string) => {
  try {
    return await request<any>(`/api/stores/${storeId}`);
  } catch {
    return null;
  }
};

export const getStoreProducts = async (
  storeId: string,
  page: number = 1,
  pageSize: number = 24,
  sortBy?: string,
  q?: string,
) => {
  try {
    const qs = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (sortBy) qs.set('sortBy', sortBy);
    if (q) qs.set('q', q);
    return normalizeProducts(
      await request<any[]>(`/api/stores/${storeId}/products?${qs}`),
    );
  } catch {
    return [];
  }
};

export const getAllStores = async (page: number = 1, pageSize: number = 20) => {
  try {
    return await request<any[]>(`/api/stores?page=${page}&pageSize=${pageSize}`);
  } catch {
    return [];
  }
};

// ─── Media ───────────────────────────────────────────────────

export const uploadFile = async (file: File, category: string, relatedId?: string) => {
  const fd = new FormData();
  fd.append('file', file);
  const qs = new URLSearchParams({ category });
  if (relatedId != null) qs.set('relatedId', String(relatedId));
  return request<{ url: string; id: number }>(`/api/media/upload?${qs}`, {
    method: 'POST',
    auth: true,
    formData: true,
    body: fd,
  });
};

export const uploadProductImage = uploadFile;
export const uploadStoreLogo = async (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return request<{ url: string; id: number }>('/api/media/store/logo', {
    method: 'POST',
    auth: true,
    formData: true,
    body: fd,
  });
};
export const uploadStoreBanner = async (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return request<{ url: string; id: number }>('/api/media/store/banner', {
    method: 'POST',
    auth: true,
    formData: true,
    body: fd,
  });
};
export const uploadStoryImage = async (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return request<{ url: string; id: number }>('/api/media/story/image', {
    method: 'POST',
    auth: true,
    formData: true,
    body: fd,
  });
};
export const uploadReviewImage = async (file: File, reviewId?: string) =>
  uploadFile(file, 'reviews', reviewId);

export const getFile = async (fileId: number, _download: boolean = false) => {
  return { url: `${API_BASE}/uploads/${fileId}` };
};

export const getUserAvatar = async (_userId: number | string) => {
  return null;
};

export const updateLocalStorageAvatar = (avatarUrl: string) => {
  localStorage.setItem('userAvatar', avatarUrl);
};

export const getMediaMyFiles = async (
  _category?: string,
  _page: number = 1,
  _pageSize: number = 20,
) => {
  return { files: [], totalCount: 0 };
};

export const deleteMediaFile = async (_id: number) => {
  return { message: 'Not supported' };
};

export const downloadZip = async (_fileIds: number[]) => {
  throw new Error('Not supported');
};

export const exportSales = async (_from?: string, _to?: string) => {
  throw new Error('Not supported');
};

export const uploadMultipleFiles = async (
  files: File[],
  category: string,
  relatedId?: string,
) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  const qs = new URLSearchParams({ category });
  if (relatedId != null) qs.set('relatedId', String(relatedId));
  return request<{ urls: string[] }>(`/api/media/upload-multiple?${qs}`, {
    method: 'POST',
    auth: true,
    formData: true,
    body: fd,
  });
};

// ─── Admin ───────────────────────────────────────────────────

export const getAdminStats = async () => {
  try {
    return await request<any>('/api/admin/stats', { auth: true });
  } catch {
    return null;
  }
};

export const getAdminUsers = async (page: number = 1, pageSize: number = 50) => {
  try {
    return await request<any[]>(`/api/admin/users?page=${page}&pageSize=${pageSize}`, {
      auth: true,
    });
  } catch {
    return [];
  }
};

export const updateAdminUser = async (userId: string, data: any) => {
  return request<{ message: string }>(`/api/admin/users/${userId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(data),
  });
};

export const deleteAdminUser = async (userId: string) => {
  return request<{ message: string }>(`/api/admin/users/${userId}`, {
    method: 'DELETE',
    auth: true,
  });
};

export const getAdminStores = async (page: number = 1, pageSize: number = 50) => {
  try {
    return await request<any[]>(`/api/admin/stores?page=${page}&pageSize=${pageSize}`, {
      auth: true,
    });
  } catch {
    return [];
  }
};

export const updateAdminStore = async (storeId: string, data: any) => {
  return request<{ message: string }>(`/api/admin/stores/${storeId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({
      name: data.name,
      status: data.status,
      tag: data.tag,
      isSponsored: data.isSponsored ?? data.is_sponsored,
    }),
  });
};

export const deleteAdminStore = async (storeId: string) => {
  return request<{ message: string }>(`/api/admin/stores/${storeId}`, {
    method: 'DELETE',
    auth: true,
  });
};

export const getAdminProducts = async (page: number = 1, pageSize: number = 50) => {
  try {
    return await request<any[]>(`/api/admin/products?page=${page}&pageSize=${pageSize}`, {
      auth: true,
    });
  } catch {
    return [];
  }
};

export const updateAdminProduct = async (productId: string, data: any) => {
  return request<{ message: string }>(`/api/admin/products/${productId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(data),
  });
};

export const deleteAdminProduct = async (productId: string) => {
  return request<{ message: string }>(`/api/admin/products/${productId}`, {
    method: 'DELETE',
    auth: true,
  });
};

export const getAdminOrders = async (page: number = 1, pageSize: number = 50) => {
  try {
    return await request<any[]>(`/api/admin/orders?page=${page}&pageSize=${pageSize}`, {
      auth: true,
    });
  } catch {
    return [];
  }
};

export const updateAdminOrder = async (orderId: string, data: any) => {
  return request<{ message: string }>(`/api/admin/orders/${orderId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(data),
  });
};

export const deleteAdminOrder = async (orderId: string) => {
  return request<{ message: string }>(`/api/admin/orders/${orderId}`, {
    method: 'DELETE',
    auth: true,
  });
};

export const getAdminCategories = async () => {
  try {
    return await request<any[]>('/api/admin/categories', { auth: true });
  } catch {
    return [];
  }
};

export const createAdminCategory = async (data: any) => {
  return request<{ id?: number; message: string }>('/api/admin/categories', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({
      name: data.name,
      sortOrder: data.sortOrder ?? data.sort_order ?? 0,
      icon: data.icon,
      parentId: data.parentId ?? data.parent_id,
    }),
  });
};

export const updateAdminCategory = async (categoryId: string, data: any) => {
  return request<{ message: string }>(`/api/admin/categories/${categoryId}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({
      name: data.name,
      sortOrder: data.sortOrder ?? data.sort_order ?? 0,
      icon: data.icon,
      parentId: data.parentId ?? data.parent_id,
    }),
  });
};

export const deleteAdminCategory = async (categoryId: string) => {
  return request<{ message: string }>(`/api/admin/categories/${categoryId}`, {
    method: 'DELETE',
    auth: true,
  });
};

export const getAdminSettings = async (): Promise<PlatformSettings> => {
  return request<PlatformSettings>('/api/admin/settings', { auth: true });
};

export const updateAdminSettings = async (
  settings: Partial<PlatformSettings>,
): Promise<PlatformSettings> => {
  return request<PlatformSettings>('/api/admin/settings', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(settings),
  });
};

/** Banners */
const normalizeBanner = (b: any) => ({
  id: b.id,
  title: b.title ?? '',
  subtitle: b.subtitle ?? '',
  image_url: b.imageUrl ?? b.image_url ?? '',
  link_url: b.linkUrl ?? b.link_url ?? '',
  position: b.position ?? b.type ?? 'home_top',
  is_active: b.isActive ?? b.is_active ?? true,
  sort_order: b.sortOrder ?? b.sort_order ?? 0,
  created_at: b.createdAt ?? b.created_at,
});

const bannerToApi = (data: any) => ({
  title: data.title,
  subtitle: data.subtitle,
  imageUrl: data.image_url ?? data.imageUrl,
  linkUrl: data.link_url ?? data.linkUrl,
  position: data.position ?? 'home_top',
  isActive: data.is_active ?? data.isActive ?? true,
  sortOrder: data.sort_order ?? data.sortOrder ?? 0,
});

export const getBanners = async () => {
  try {
    const data = await request<any[]>('/api/banners');
    return (Array.isArray(data) ? data : []).map(normalizeBanner);
  } catch {
    return [];
  }
};

/* ─── Campaigns / flash sales ─── */

export type Campaign = {
  id: string;
  title: string;
  description?: string;
  type: string;
  displayStyle?: string;
  storeId?: string;
  storeName?: string;
  status: string;
  startsAt: string;
  endsAt: string;
  themeColor: string;
  badgeLabel?: string;
  requiresProductApproval: boolean;
  coverImageUrl?: string;
  sortOrder: number;
  productCount: number;
  pendingCount: number;
  endsInSeconds: number;
  isLive: boolean;
};

export type CampaignProduct = {
  id: string;
  campaignId: string;
  productId: string;
  catalogProductId?: string;
  storeId?: string;
  productName: string;
  imageUrl?: string;
  storeName?: string;
  originalPrice: number;
  campaignPrice: number;
  discountPercent: number;
  currency: string;
  status: string;
  stock?: number;
  rating?: number;
  hasDelivery?: boolean;
  productType?: string;
  variantOptions?: string[];
  packSize?: number;
  description?: string;
};

const normalizeCampaign = (c: any): Campaign => ({
  id: String(c.id ?? c.Id),
  title: c.title ?? c.Title ?? '',
  description: c.description ?? c.Description,
  type: c.type ?? c.Type ?? 'platform',
  displayStyle: c.displayStyle ?? c.DisplayStyle ?? 'flash',
  storeId: c.storeId ?? c.StoreId,
  storeName: c.storeName ?? c.StoreName,
  status: c.status ?? c.Status ?? 'draft',
  startsAt: c.startsAt ?? c.StartsAt,
  endsAt: c.endsAt ?? c.EndsAt,
  themeColor: c.themeColor ?? c.ThemeColor ?? '#CAA53E',
  badgeLabel: c.badgeLabel ?? c.BadgeLabel,
  requiresProductApproval: c.requiresProductApproval ?? c.RequiresProductApproval ?? true,
  coverImageUrl: c.coverImageUrl ?? c.CoverImageUrl,
  sortOrder: c.sortOrder ?? c.SortOrder ?? 0,
  productCount: c.productCount ?? c.ProductCount ?? 0,
  pendingCount: c.pendingCount ?? c.PendingCount ?? 0,
  endsInSeconds: c.endsInSeconds ?? c.EndsInSeconds ?? 0,
  isLive: !!(c.isLive ?? c.IsLive),
});

const normalizeCampaignProduct = (p: any): CampaignProduct => ({
  id: String(p.id ?? p.Id),
  campaignId: String(p.campaignId ?? p.CampaignId),
  productId: String(p.productId ?? p.ProductId),
  catalogProductId:
    p.catalogProductId != null || p.CatalogProductId != null
      ? String(p.catalogProductId ?? p.CatalogProductId)
      : undefined,
  storeId: p.storeId ?? p.StoreId,
  productName: p.productName ?? p.ProductName ?? '',
  imageUrl: p.imageUrl ?? p.ImageUrl,
  storeName: p.storeName ?? p.StoreName,
  originalPrice: Number(p.originalPrice ?? p.OriginalPrice ?? 0),
  campaignPrice: Number(p.campaignPrice ?? p.CampaignPrice ?? 0),
  discountPercent: Number(p.discountPercent ?? p.DiscountPercent ?? 0),
  currency: p.currency ?? p.Currency ?? 'EUR',
  status: p.status ?? p.Status ?? 'pending',
  stock: p.stock != null || p.Stock != null ? Number(p.stock ?? p.Stock) : undefined,
  rating: p.rating != null || p.Rating != null ? Number(p.rating ?? p.Rating) : undefined,
  hasDelivery: !!(p.hasDelivery ?? p.HasDelivery),
  productType: p.productType ?? p.ProductType,
  variantOptions: Array.isArray(p.variantOptions ?? p.VariantOptions)
    ? (p.variantOptions ?? p.VariantOptions).map(String)
    : undefined,
  packSize: p.packSize != null || p.PackSize != null ? Number(p.packSize ?? p.PackSize) : undefined,
  description: p.description ?? p.Description ?? undefined,
});

export const getActiveCampaigns = async () => {
  try {
    const data = await request<any[]>('/api/campaigns/active');
    return (Array.isArray(data) ? data : []).map(normalizeCampaign);
  } catch {
    return [];
  }
};

export const getCampaign = async (campaignId: string) => {
  try {
    const data = await request<any>(`/api/campaigns/${campaignId}`);
    return data ? normalizeCampaign(data) : null;
  } catch {
    return null;
  }
};

export const getCampaignProducts = async (campaignId: string) => {
  try {
    const data = await request<any[]>(`/api/campaigns/${campaignId}/products`);
    return (Array.isArray(data) ? data : []).map(normalizeCampaignProduct);
  } catch {
    return [];
  }
};

export type CampaignProductDetail = {
  campaign: Campaign;
  product: CampaignProduct;
};

export const getCampaignProductDetail = async (
  campaignId: string,
  campaignProductId: string,
): Promise<CampaignProductDetail | null> => {
  try {
    const data = await request<any>(`/api/campaigns/${campaignId}/products/${campaignProductId}`);
    if (!data?.campaign && !data?.Campaign) return null;
    return {
      campaign: normalizeCampaign(data.campaign ?? data.Campaign),
      product: normalizeCampaignProduct(data.product ?? data.Product),
    };
  } catch (err) {
    // Older API builds may lack the detail route; compose from list + campaign.
    if (err instanceof ApiError && err.status === 404) {
      const [campaign, products] = await Promise.all([
        getCampaign(campaignId),
        getCampaignProducts(campaignId),
      ]);
      const product = products.find((p) => p.id === campaignProductId);
      if (campaign && product) return { campaign, product };
    }
    return null;
  }
};

export const getAdminCampaigns = async () => {
  const data = await request<any[]>('/api/admin/campaigns', { auth: true });
  return (Array.isArray(data) ? data : []).map(normalizeCampaign);
};

export const createAdminCampaign = async (body: Record<string, unknown>) =>
  request<{ message: string; id?: string }>('/api/admin/campaigns', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });

export const updateAdminCampaign = async (id: string, body: Record<string, unknown>) =>
  request<{ message: string }>(`/api/admin/campaigns/${id}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(body),
  });

export const setAdminCampaignStatus = async (id: string, status: string) =>
  request<{ message: string }>(`/api/admin/campaigns/${id}/status`, {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ status }),
  });

export const getAdminCampaignProducts = async (id: string, status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const data = await request<any[]>(`/api/admin/campaigns/${id}/products${q}`, { auth: true });
  return (Array.isArray(data) ? data : []).map(normalizeCampaignProduct);
};

export const adminAddCampaignProduct = async (
  campaignId: string,
  productId: string,
  campaignPrice: number,
  packSize?: number,
) =>
  request<{ message: string }>(`/api/admin/campaigns/${campaignId}/products`, {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ productId, campaignPrice, packSize: packSize && packSize > 0 ? packSize : undefined }),
  });

export const adminReviewCampaignProduct = async (campaignProductId: string, status: 'approved' | 'rejected') =>
  request<{ message: string }>(`/api/admin/campaigns/products/${campaignProductId}/review`, {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ status }),
  });

export const deleteAdminCampaign = async (id: string) =>
  request<{ message: string }>(`/api/admin/campaigns/${id}`, { method: 'DELETE', auth: true });

export const getVendorOpenCampaigns = async () => {
  const data = await request<any[]>('/api/vendor/campaigns/open', { auth: true });
  return (Array.isArray(data) ? data : []).map(normalizeCampaign);
};

export const getVendorStoreCampaigns = async () => {
  const data = await request<any[]>('/api/vendor/campaigns/mine', { auth: true });
  return (Array.isArray(data) ? data : []).map(normalizeCampaign);
};

export const createVendorCampaign = async (body: Record<string, unknown>) =>
  request<{ message: string; id?: string }>('/api/vendor/campaigns', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });

export const updateVendorCampaign = async (id: string, body: Record<string, unknown>) =>
  request<{ message: string }>(`/api/vendor/campaigns/${id}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(body),
  });

export const vendorSubmitCampaignProduct = async (campaignId: string, productId: string, campaignPrice: number) =>
  request<{ message: string }>(`/api/vendor/campaigns/${campaignId}/products`, {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ productId, campaignPrice }),
  });

export const getVendorCampaignProducts = async (campaignId: string) => {
  const data = await request<any[]>(`/api/vendor/campaigns/${campaignId}/products`, { auth: true });
  return (Array.isArray(data) ? data : []).map(normalizeCampaignProduct);
};

export const vendorRemoveCampaignProduct = async (campaignProductId: string) =>
  request<{ message: string }>(`/api/vendor/campaigns/products/${campaignProductId}`, {
    method: 'DELETE',
    auth: true,
  });


export const getAdminBanners = async () => {
  try {
    const data = await request<any[]>('/api/admin/banners', { auth: true });
    return (Array.isArray(data) ? data : []).map(normalizeBanner);
  } catch {
    return [];
  }
};

export const createBanner = async (data: any) => {
  return request<{ id?: string; message: string }>('/api/admin/banners', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(bannerToApi(data)),
  });
};

export const updateBanner = async (id: number | string, data: any) => {
  return request<{ message: string }>(`/api/admin/banners/${id}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(bannerToApi(data)),
  });
};

export const deleteBanner = async (id: number | string) => {
  return request<{ message: string }>(`/api/admin/banners/${id}`, {
    method: 'DELETE',
    auth: true,
  });
};

export interface SectionHelloMessage {
  id: string;
  sectionKey: string;
  messageKey: string;
  title?: string;
  body: string;
  ctaLabel?: string;
  ctaLink?: string;
  isActive: boolean;
  sortOrder: number;
}

export const getSectionHelloMessage = async (sectionKey: string) => {
  const dismissed = getHelloDismissedKeys();
  const headers: Record<string, string> = {};
  if (dismissed.length) headers['X-Hello-Dismissed'] = dismissed.join(',');
  try {
    return await request<SectionHelloMessage>(`/api/hello/${encodeURIComponent(sectionKey)}`, {
      auth: false,
      headers,
    });
  } catch {
    return null;
  }
};

const HELLO_DISMISSED_KEY = 'bazaarak_hello_dismissed';

export function getHelloDismissedKeys(): string[] {
  try {
    const raw = localStorage.getItem(HELLO_DISMISSED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function markHelloDismissed(messageKey: string) {
  const set = new Set(getHelloDismissedKeys());
  set.add(messageKey);
  localStorage.setItem(HELLO_DISMISSED_KEY, JSON.stringify([...set]));
}

export const dismissHelloMessage = async (messageId: string, messageKey: string) => {
  markHelloDismissed(messageKey);
  if (localStorage.getItem('token')) {
    try {
      await request(`/api/hello/${messageId}/dismiss`, { method: 'POST', auth: true });
    } catch {
      /* offline ok */
    }
  }
};

export const getAdminHelloMessages = async () => {
  try {
    return await request<SectionHelloMessage[]>('/api/admin/hello-messages', { auth: true });
  } catch {
    return [];
  }
};

export const upsertAdminHelloMessage = async (id: string | null, data: Partial<SectionHelloMessage>) => {
  const body = {
    sectionKey: data.sectionKey,
    messageKey: data.messageKey,
    title: data.title,
    body: data.body,
    ctaLabel: data.ctaLabel,
    ctaLink: data.ctaLink,
    isActive: data.isActive ?? true,
    sortOrder: data.sortOrder ?? 0,
  };
  if (id) {
    return request(`/api/admin/hello-messages/${id}`, { method: 'PUT', auth: true, body: JSON.stringify(body) });
  }
  return request('/api/admin/hello-messages', { method: 'POST', auth: true, body: JSON.stringify(body) });
};

export const deleteAdminHelloMessage = async (id: string) =>
  request(`/api/admin/hello-messages/${id}`, { method: 'DELETE', auth: true });

export const getPlatformCurrencies = async () => {
  try {
    return await request<{ defaultCurrency: string; allowedCurrencies: string[] }>('/api/platform/currencies');
  } catch {
    return { defaultCurrency: 'EUR', allowedCurrencies: ['EUR'] };
  }
};

export const getAdminReviews = async () => {
  // No admin reviews endpoint yet — avoid loading current user's personal reviews
  return [];
};

// ─── i18n / Geo / Discover ───────────────────────────────────

export interface LanguageInfo {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  isRtl: boolean;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  fallbackCode?: string;
}

export interface I18nBootstrap {
  languages: LanguageInfo[];
  defaultLanguage: string;
  resolvedLanguage: string;
  translations: Record<string, string>;
}

export interface GeoResolve {
  countryCode?: string | null;
  region?: string | null;
  city?: string | null;
  suggestedLanguage?: string;
  source: string;
}

/** Convert flat dotted keys into nested object for i18next */
export function unflattenTranslations(flat: Record<string, string>): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat || {})) {
    const parts = key.split('.');
    let cur: Record<string, unknown> = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (typeof cur[p] !== 'object' || cur[p] === null) cur[p] = {};
      cur = cur[p] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
  }
  return root;
}

export const getI18nLanguages = async (): Promise<LanguageInfo[]> => {
  try {
    return (await request<LanguageInfo[]>('/api/i18n/languages', { auth: false })) || [];
  } catch {
    return [];
  }
};

export const getI18nBootstrap = async (lang?: string): Promise<I18nBootstrap | null> => {
  try {
    const q = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    return await request<I18nBootstrap>(`/api/i18n/bootstrap${q}`, { auth: false });
  } catch {
    return null;
  }
};

export const getI18nTranslations = async (lang: string): Promise<Record<string, string>> => {
  try {
    return (await request<Record<string, string>>(`/api/i18n/translations/${encodeURIComponent(lang)}`, { auth: false })) || {};
  } catch {
    return {};
  }
};

export const resolveGeo = async (): Promise<GeoResolve | null> => {
  try {
    return await request<GeoResolve>('/api/geo/resolve', { auth: false });
  } catch {
    return null;
  }
};

export const getDiscoverLanding = async (opts?: {
  limit?: number;
  country?: string;
  lat?: number;
  lng?: number;
}) => {
  try {
    const q = new URLSearchParams();
    if (opts?.limit) q.set('limit', String(opts.limit));
    if (opts?.country) q.set('country', opts.country);
    if (opts?.lat != null) q.set('lat', String(opts.lat));
    if (opts?.lng != null) q.set('lng', String(opts.lng));
    return await request<any>(`/api/discover/landing?${q}`, { auth: false });
  } catch {
    return null;
  }
};

export const updateUserLanguage = async (code: string) => {
  return request('/api/user/language', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ code }),
  });
};

export const getAdminI18nLanguages = async () =>
  request<LanguageInfo[]>('/api/i18n/admin/languages', { auth: true });

export const upsertAdminLanguage = async (body: any) =>
  request('/api/i18n/admin/languages', { method: 'POST', auth: true, body: JSON.stringify(body) });

export const setDefaultAdminLanguage = async (id: string) =>
  request(`/api/i18n/admin/languages/${id}/default`, { method: 'PUT', auth: true });

export const deleteAdminLanguage = async (id: string) =>
  request(`/api/i18n/admin/languages/${id}`, { method: 'DELETE', auth: true });

export const getAdminTranslations = async (params: {
  lang?: string;
  ns?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) => {
  const q = new URLSearchParams();
  if (params.lang) q.set('lang', params.lang);
  if (params.ns) q.set('ns', params.ns);
  if (params.search) q.set('search', params.search);
  q.set('page', String(params.page || 1));
  q.set('pageSize', String(params.pageSize || 50));
  return request<any>(`/api/i18n/admin/translations?${q}`, { auth: true });
};

export const bulkUpsertAdminTranslations = async (
  items: { key: string; languageCode: string; value: string }[],
) =>
  request('/api/i18n/admin/translations', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(items),
  });

export const getProductTranslations = async (productId: string) =>
  request<any[]>(`/api/i18n/admin/products/${productId}/translations`, { auth: true });

export const upsertProductTranslations = async (
  productId: string,
  translations: any[],
) =>
  request(`/api/i18n/admin/products/${productId}/translations`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ translations }),
  });

// ─── Admin catalog (merge / relink multi-vendor offers) ──────

export interface AdminCatalogOffer {
  productId: string;
  storeId?: string | null;
  storeName?: string | null;
  price?: number | null;
  discountPrice?: number | null;
  stock?: number | null;
  status?: string | null;
}

export interface AdminCatalogProduct {
  catalogProductId: string;
  name: string;
  description?: string;
  categoryId?: string | null;
  barcode?: string | null;
  image?: string | null;
  offerCount: number;
  fromPrice?: number | null;
  createdAt?: string | null;
  offers?: AdminCatalogOffer[];
}

const normalizeAdminCatalog = (c: any): AdminCatalogProduct => ({
  catalogProductId: String(c.catalogProductId ?? c.catalog_product_id ?? c.id ?? ''),
  name: c.name ?? '',
  description: c.description ?? '',
  categoryId: c.categoryId ?? c.category_id ?? null,
  barcode: c.barcode ?? null,
  image:
    c.image ??
    (Array.isArray(c.media)
      ? (typeof c.media[0] === 'string' ? c.media[0] : c.media[0]?.url)
      : null) ??
    null,
  offerCount: c.offerCount ?? c.offer_count ?? (Array.isArray(c.offers) ? c.offers.length : 0),
  fromPrice: c.fromPrice ?? c.from_price ?? null,
  createdAt: c.createdAt ?? c.created_at ?? null,
  offers: Array.isArray(c.offers)
    ? c.offers.map((o: any) => ({
        productId: String(o.productId ?? o.product_id ?? o.id ?? ''),
        storeId: o.storeId ?? o.store_id ?? null,
        storeName: o.storeName ?? o.store_name ?? null,
        price: o.price ?? null,
        discountPrice: o.discountPrice ?? o.discount_price ?? null,
        stock: o.stock ?? null,
        status: o.status ?? null,
      }))
    : undefined,
});

export const getAdminCatalog = async (
  page: number = 1,
  pageSize: number = 50,
  q?: string,
): Promise<{ items: AdminCatalogProduct[]; totalCount: number }> => {
  try {
    const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q) qs.set('q', q);
    const data = await request<any>(`/api/admin/catalog?${qs}`, { auth: true });
    // Support both { items, totalCount } and bare array responses
    const list = Array.isArray(data) ? data : (data?.items ?? data?.products ?? []);
    const total = Array.isArray(data)
      ? data.length
      : (data?.totalCount ?? data?.total ?? list.length);
    return { items: list.map(normalizeAdminCatalog), totalCount: total };
  } catch {
    return { items: [], totalCount: 0 };
  }
};

export const getAdminCatalogById = async (
  id: string,
): Promise<AdminCatalogProduct | null> => {
  try {
    const data = await request<any>(`/api/admin/catalog/${id}`, { auth: true });
    return data ? normalizeAdminCatalog(data) : null;
  } catch {
    return null;
  }
};

/** Merge all offers from source catalog into target, then deactivate the empty source. */
export const mergeAdminCatalog = async (
  sourceCatalogId: string,
  targetCatalogId: string,
) =>
  request<{ message: string }>('/api/admin/catalog/merge', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ sourceCatalogId, targetCatalogId }),
  });

/** Move a single offer (product) to a different catalog identity. */
export const relinkAdminCatalogOffer = async (
  productId: string,
  catalogProductId: string,
) =>
  request<{ message: string }>('/api/admin/catalog/relink', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ productId, catalogProductId }),
  });

export const getRecommendationRules = async () =>
  request<any[]>('/api/admin/recommendation-rules', { auth: true });

export const upsertRecommendationRule = async (body: any) =>
  request('/api/admin/recommendation-rules', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });

export const deleteRecommendationRule = async (id: string) =>
  request(`/api/admin/recommendation-rules/${id}`, { method: 'DELETE', auth: true });

export type RecoStep = {
  id: string;
  type: string;
  label?: string;
  x?: number;
  y?: number;
  config?: Record<string, unknown>;
  transitions?: { targetId: string; port?: string }[];
  decisionRules?: { field: string; op: string; value: string; nextNodeId: string }[];
};

export type RecommendationAlgorithm = {
  id: string;
  name: string;
  description?: string;
  stageScope: string;
  steps: RecoStep[];
  version: number;
  versionGroup: string;
  status: string;
  sortOrder: number;
  updatedAt?: string;
};

export const getRecommendationAlgorithms = async (scope?: string) => {
  const q = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  return request<RecommendationAlgorithm[]>(`/api/admin/recommendation-algorithms${q}`, { auth: true });
};

export const saveRecommendationAlgorithm = async (body: {
  id?: string;
  name: string;
  description?: string;
  stageScope?: string;
  steps: RecoStep[];
  sortOrder?: number;
}) =>
  request<RecommendationAlgorithm>('/api/admin/recommendation-algorithms', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });

export const setRecommendationAlgorithmStatus = async (id: string, status: string) =>
  request<RecommendationAlgorithm>(`/api/admin/recommendation-algorithms/${id}/status`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify({ status }),
  });

export const deleteRecommendationAlgorithm = async (id: string) =>
  request(`/api/admin/recommendation-algorithms/${id}`, { method: 'DELETE', auth: true });

export const testRecommendationAlgorithm = async (
  id: string,
  body?: { countryCode?: string; city?: string; categoryId?: string; limit?: number },
) =>
  request<{
    productIds: string[];
    algorithmId: string;
    version: number;
    status: string;
    stageScope: string;
  }>(`/api/admin/recommendation-algorithms/${id}/test`, {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body || { countryCode: 'DE', city: 'Köln', limit: 12 }),
  });

export const inviteAdminUser = async (body: {
  email: string;
  name?: string;
  role: string;
  phone?: string;
  password?: string;
  storeName?: string;
}) =>
  request<{ temporaryPassword?: string; userId?: string }>('/api/admin/users/invite', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });
